import { UserOperationsRepositoryImplementation } from '../infrastructure/user-operations-repository-implementation';
import { User, UserRequest } from '../domain/user/model';
import { Subscription } from '../domain/suscriptions/model'; // Import Subscription model
import { EmptyArray } from "../domain/empty/model"; // Assuming EmptyArray exists

import { cacheManager } from '../infrastructure/cache-manager';

const repo = new UserOperationsRepositoryImplementation(); // No uid, cid needed based on user's update

const USERS_PREFIX = 'users';
const SUBSCRIPTIONS_PREFIX = 'subscriptions';

async function createUser(data: UserRequest): Promise<User | undefined> {
  try {
    const result = await repo.create(data).toPromise()!;
    // Invalidate caches after a creation
    cacheManager.invalidate(USERS_PREFIX);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getUserById(id: number): Promise<User | typeof EmptyArray> {
  try {
    const cacheKey = `${USERS_PREFIX}-detail-${id}`;
    const cached = cacheManager.getItem<User>(cacheKey);

    if (cached) {
      return cached;
    }
    const result = await repo.getById(id).toPromise();

    if (!result) return EmptyArray;
    cacheManager.setItem(cacheKey, result);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getUsers(page: number, limit: number): Promise<User[] | typeof EmptyArray> {
  try {
    const cacheKey = `${USERS_PREFIX}-list-${page}-${limit}`;
    const cached = cacheManager.getItem<User[]>(cacheKey);

    if (cached) {
      return cached;
    }
    const result = await repo.getUsers(page, limit).toPromise();
    // Assuming the API returns an array of users directly
    if (!result || result.length === 0) return EmptyArray;
    cacheManager.setItem(cacheKey, result);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function updateUsers(id: number, data: Partial<User>): Promise<User | undefined> {
  const result = await repo.updateById(id, data).toPromise()!;
  // Invalidate caches after an update
  cacheManager.invalidate(USERS_PREFIX);
  return result;
}

async function deleteUser(id: number): Promise<void> {
  await repo.delete(id).toPromise();
  cacheManager.invalidate(USERS_PREFIX);
}

async function createSubscription(data: Partial<Subscription>): Promise<Subscription | undefined> {
  try {
    const result = await repo.createSubscription(data).toPromise()!;
    // Invalidate subscription caches after creation
    cacheManager.invalidate(SUBSCRIPTIONS_PREFIX);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function updateSubscription(data: Partial<Subscription>): Promise<Subscription | undefined> {
  try {
    const result = await repo.updateSubscription(data).toPromise()!;
    // Invalidate subscription caches after update
    cacheManager.invalidate(SUBSCRIPTIONS_PREFIX);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}


async function getSubscriptionById(id: number): Promise<Subscription | undefined> {
  try {
    const cacheKey = `${SUBSCRIPTIONS_PREFIX}-detail-${id}`;
    const cached = cacheManager.getItem<Subscription>(cacheKey);

    if (cached) {
      return cached;
    }
    const result = await repo.getSubscription(id).toPromise();
    if (result) {
      cacheManager.setItem(cacheKey, result);
    }
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

const DOCUMENT_PACKAGES_PREFIX = 'document-packages';

async function getDocumentPackages(userId: number, page: number, limit: number): Promise<any> {
  try {
    const cacheKey = `${DOCUMENT_PACKAGES_PREFIX}-user-${userId}-${page}-${limit}`;
    const cached = cacheManager.getItem<any>(cacheKey);

    if (cached) {
      return cached;
    }
    const result = await repo.getDocumentPackages(userId, page, limit).toPromise();
    if (result) {
      cacheManager.setItem(cacheKey, result);
    }
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function addDocumentPackage(payload: { users_id: number, quantity: number, marketplace_package_id: number }): Promise<any> {
  try {
    const result = await repo.addDocumentPackage(payload).toPromise();
    // Invalidate packages cache for this user
    cacheManager.invalidate(DOCUMENT_PACKAGES_PREFIX);
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getAvailablePackages(page: number, limit: number): Promise<any> {
  try {
    const cacheKey = `${DOCUMENT_PACKAGES_PREFIX}-catalog-${page}-${limit}`;
    const cached = cacheManager.getItem<any>(cacheKey);

    if (cached) {
      return cached;
    }
    const result = await repo.getAvailablePackages(page, limit).toPromise();
    if (result) {
      cacheManager.setItem(cacheKey, result);
    }
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function initializeUserConfig(userId: number): Promise<any> {
  try {
    const result = await repo.initializeConfig(userId).toPromise();
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export {
  createUser,
  getUserById,
  getUsers,
  updateUsers,
  deleteUser,
  createSubscription,
  updateSubscription,
  getSubscriptionById,
  getDocumentPackages,
  addDocumentPackage,
  getAvailablePackages,
  initializeUserConfig,
};
